import { Injectable } from '@angular/core';
import { VsAuth, StorageType } from '@videosoft/vs-auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserAuth } from 'src/models/user/user-auth/user-auth';
import { AppEnvironment } from 'src/models/common/environment/environment';
import { IAuthService } from './auth-service';
import { makeJwtService } from '../common/jwt/jwt-service-factory';
import { IJwtDecoded } from '../common/jwt/jwt-service';
import { makeConfigService } from '../common/config/config-service-factory';

interface HttpRequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  baseUrl?: string;
  params?: Record<string, string | number | boolean | null | undefined>;
}

class ApiHttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    super(`HTTP ${status}: ${statusText || 'Erro na API'}`);
    this.name = 'ApiHttpError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthServiceImpl implements IAuthService {
  private static readonly KEYCLOAK_REALM = 'videosoft-platform';
  private static readonly KEYCLOAK_CLIENT_ID = 'app.vsd.agenda';
  private static readonly ENTITY_CNPJ_HEADER = 'x-entity-cnpj';
  private auth: VsAuth;
  private jwtService = makeJwtService();
  private configService = makeConfigService();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> =
    this.isAuthenticatedSubject.asObservable();

  constructor(private environment: AppEnvironment) {
    this.auth = VsAuth.keycloak({
      url: this.getKeycloakUrl(),
      realm: AuthServiceImpl.KEYCLOAK_REALM,
      clientId: AuthServiceImpl.KEYCLOAK_CLIENT_ID,
      redirectUri: this.getRedirectUri(),
      postLogoutRedirectUri: this.getPostLogoutRedirectUri(),
      storageType: StorageType.AUTO,
      defaultTimeout: 30000,
    });

    this.patchVsAuthHttpClient();
    void this.initializeAuthentication();
  }

  private getKeycloakUrl(): string {
    return this.environment.KEYCLOAK_URL;
  }

  private getRedirectUri(): string {
    return `${window.location.origin}/`;
  }

  private getPostLogoutRedirectUri(): string {
    return `${window.location.origin}/`;
  }

  private async initializeAuthentication(): Promise<void> {
    try {
      await this.auth.handleCallback();
    } catch (error) {
      console.error('Erro ao inicializar autenticacao OIDC', error);
    } finally {
      this.checkAuthentication();
    }
  }

  private checkAuthentication() {
    const isAuth = this.auth.isAuthenticated();
    this.isAuthenticatedSubject.next(isAuth);
  }

  async login(): Promise<UserAuth> {
    try {
      await this.auth.ensureAuthenticated();
      this.checkAuthentication();

      return {
        accessToken: this.auth.getAccessToken() ?? '',
        refreshToken: this.auth.getRefreshToken() ?? '',
      };
    } catch (error) {
      this.isAuthenticatedSubject.next(false);
      throw error;
    }
  }

  logout(): void {
    void this.performLogout();
  }

  private async performLogout(): Promise<void> {
    await this.auth.logout();
    this.isAuthenticatedSubject.next(false);
  }

  isAuthenticated(): boolean {
    return this.auth.isAuthenticated();
  }

  async getToken(): Promise<string | null> {
    return this.auth.getValidAccessToken();
  }

  async refreshToken(): Promise<void> {
    await this.auth.getValidAccessToken();
    this.checkAuthentication();
  }

  async hasPermission(permission: string): Promise<boolean> {
    return this.auth.hasPermission(permission);
  }

  get http() {
    return this.createHttpClient();
  }

  get httpCentral() {
    const centralBase = this.environment.API_CENTRAL_URL.replace(/\/$/, '');

    return this.createHttpClient(centralBase);
  }

  private createHttpClient(baseUrl = this.environment.API_URL) {
    return {
      get: <T>(path: string, options?: HttpRequestOptions | any) =>
        this.auth.http.get<T>(path, this.normalizeOptions(options, baseUrl)),
      post: <T>(
        path: string,
        body?: unknown,
        options?: HttpRequestOptions | any,
      ) =>
        this.auth.http.post<T>(
          path,
          body,
          this.normalizeOptions(options, baseUrl),
        ),
      put: <T>(
        path: string,
        body?: unknown,
        options?: HttpRequestOptions | any,
      ) =>
        this.auth.http.put<T>(
          path,
          body,
          this.normalizeOptions(options, baseUrl),
        ),
      patch: <T>(
        path: string,
        body?: unknown,
        options?: HttpRequestOptions | any,
      ) =>
        this.auth.http.patch<T>(
          path,
          body,
          this.normalizeOptions(options, baseUrl),
        ),
      delete: <T>(path: string, options?: HttpRequestOptions | any) =>
        this.auth.http.delete<T>(path, this.normalizeOptions(options, baseUrl)),
    };
  }

  private normalizeOptions(
    options: HttpRequestOptions | any,
    baseUrl?: string,
  ): HttpRequestOptions {
    if (!options) return baseUrl ? { baseUrl } : {};

    const hasRequestOptions =
      'headers' in options ||
      'timeout' in options ||
      'baseUrl' in options ||
      'params' in options;

    if (hasRequestOptions) {
      return { ...options, baseUrl: options.baseUrl ?? baseUrl };
    }

    return { params: options, baseUrl };
  }

  private patchVsAuthHttpClient(): void {
    const httpClient = this.auth.http as any;
    if (httpClient.__preserveApiErrorBody) return;

    httpClient.__preserveApiErrorBody = true;
    httpClient.request = async <T>(
      method: string,
      path: string,
      body?: unknown,
      options: HttpRequestOptions = {},
    ): Promise<T> => {
      const token = await httpClient.tokenManager.getValidAccessToken();

      if (!token) {
        throw new ApiHttpError(401, 'Unauthenticated', null);
      }

      return this.executeVsAuthRequest<T>(
        httpClient,
        method,
        path,
        body,
        options,
        token,
      );
    };
  }

  private buildUrl(
    path: string,
    options: HttpRequestOptions,
    defaultBaseUrl: string,
  ): string {
    const baseUrl = options.baseUrl ?? defaultBaseUrl;
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const cleanPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
    const url = path.startsWith('http') ? path : `${cleanBaseUrl}${cleanPath}`;

    if (!options.params) return url;

    const queryString = new URLSearchParams(
      Object.entries(options.params).reduce(
        (acc, [key, value]) => {
          if (value !== null && value !== undefined) acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>,
      ),
    ).toString();

    if (!queryString) return url;

    return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
  }

  private buildHeaders(
    token: string,
    body: unknown,
    options: HttpRequestOptions,
  ): Record<string, string> {
    const headers: Record<string, string> = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    if (!this.isNativeBody(body) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.shouldAttachTenantHeader(options.baseUrl)) {
      const entityTaxId = this.configService.getPrincipalEntityTaxId();

      if (entityTaxId && !headers[AuthServiceImpl.ENTITY_CNPJ_HEADER]) {
        headers[AuthServiceImpl.ENTITY_CNPJ_HEADER] = entityTaxId;
      }
    }

    return headers;
  }

  private shouldAttachTenantHeader(baseUrl?: string): boolean {
    const normalizedBaseUrl = (baseUrl ?? this.environment.API_URL).replace(
      /\/$/,
      '',
    );
    const normalizedApiUrl = this.environment.API_URL.replace(/\/$/, '');

    return normalizedBaseUrl === normalizedApiUrl;
  }

  private buildBody(body: unknown): BodyInit | undefined {
    if (body === undefined || body === null) return undefined;
    if (this.isNativeBody(body)) return body;
    return JSON.stringify(body);
  }

  private isNativeBody(body: unknown): body is BodyInit {
    return (
      (typeof FormData !== 'undefined' && body instanceof FormData) ||
      (typeof Blob !== 'undefined' && body instanceof Blob) ||
      (typeof URLSearchParams !== 'undefined' &&
        body instanceof URLSearchParams)
    );
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout apos ${timeout}ms`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async executeVsAuthRequest<T>(
    httpClient: any,
    method: string,
    path: string,
    body: unknown,
    options: HttpRequestOptions,
    token: string,
    retried = false,
  ): Promise<T> {
    const url = this.buildUrl(path, options, httpClient.apiUrl);
    const timeout = options.timeout ?? httpClient.defaultTimeout ?? 30000;
    const headers = this.buildHeaders(token, body, options);
    const response = await this.fetchWithTimeout(
      url,
      {
        method,
        headers,
        body: this.shouldSendBody(method, body)
          ? this.buildBody(body)
          : undefined,
      },
      timeout,
    );

    const responseBody = await this.parseResponse<T>(response);

    if (!response.ok) {
      if (
        response.status === 401 &&
        !retried &&
        this.shouldTryRefreshToken(responseBody, response.statusText)
      ) {
        const refreshedToken =
          await httpClient.tokenManager.refreshAccessToken();
        if (refreshedToken) {
          return this.executeVsAuthRequest<T>(
            httpClient,
            method,
            path,
            body,
            options,
            refreshedToken,
            true,
          );
        }
      }

      throw new ApiHttpError(
        response.status,
        response.statusText,
        responseBody,
      );
    }

    return responseBody;
  }

  private shouldSendBody(method: string, body: unknown): boolean {
    return (
      body !== undefined &&
      body !== null &&
      (method === 'POST' || method === 'PUT' || method === 'PATCH')
    );
  }

  private shouldTryRefreshToken(
    responseBody: unknown,
    statusText: string,
  ): boolean {
    const content = `${statusText} ${JSON.stringify(responseBody) ?? ''}`
      .toLowerCase()
      .trim();

    if (
      content.includes('access denied') ||
      content.includes('insufficient permission') ||
      content.includes('insufficient_permission') ||
      content.includes('authorization not found') ||
      content.includes('authorization_not_found')
    ) {
      return false;
    }

    return (
      content.includes('token') ||
      content.includes('expired') ||
      content.includes('jwt') ||
      content.includes('unauthorized') ||
      content.includes('unauthenticated')
    );
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const text = await response.text();
    if (!text) return undefined as T;

    try {
      return JSON.parse(text) as T;
    } catch {
      return text as T;
    }
  }

  tokenData(): IJwtDecoded | null {
    const token = this.auth.getAccessToken();
    if (!token) return null;
    const decodedToken = this.jwtService.getDataFromToken(token);
    console.log('Decoded JWT Token:', decodedToken);
    return decodedToken;
  }
}
